import React from 'react';
import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryText?: string;
  type?: 'network' | 'server' | 'generic';
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  message,
  onRetry,
  retryText = 'Try Again',
  type = 'generic',
  className = '',
}) => {
  const getErrorConfig = () => {
    switch (type) {
      case 'network':
        return {
          icon: <WifiOff className="w-8 h-8 text-red-500" />,
          defaultTitle: 'Connection Problem',
          defaultMessage: 'Please check your internet connection and try again.',
        };
      case 'server':
        return {
          icon: <AlertCircle className="w-8 h-8 text-orange-500" />,
          defaultTitle: 'Server Error',
          defaultMessage: 'Something went wrong on our end. Please try again in a moment.',
        };
      default:
        return {
          icon: <AlertCircle className="w-8 h-8 text-red-500" />,
          defaultTitle: 'Something went wrong',
          defaultMessage: 'We encountered an unexpected error. Please try again.',
        };
    }
  };

  const config = getErrorConfig();

  return (
    <div className={`text-center py-8 px-6 ${className}`}>
      <div className="max-w-md mx-auto">
        <div className="mb-4 flex justify-center">{config.icon}</div>

        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {title || config.defaultTitle}
        </h3>

        <p
          className="text-sm leading-relaxed mb-6"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {message || config.defaultMessage}
        </p>

        {onRetry && (
          <button
            onClick={onRetry}
            className="btn-primary inline-flex items-center gap-2 px-4 py-2"
          >
            <RefreshCw className="w-4 h-4" />
            {retryText}
          </button>
        )}
      </div>
    </div>
  );
};

export const DashboardErrorState: React.FC<{
  onRetry: () => void;
  error?: string;
}> = ({ onRetry, error }) => {
  // Determine error type based on error message
  const getErrorType = (errorMsg?: string): 'network' | 'server' | 'generic' => {
    if (!errorMsg) return 'generic';

    const lowerError = errorMsg.toLowerCase();
    if (
      lowerError.includes('network') ||
      lowerError.includes('connection') ||
      lowerError.includes('fetch')
    ) {
      return 'network';
    }
    if (
      lowerError.includes('500') ||
      lowerError.includes('server') ||
      lowerError.includes('internal')
    ) {
      return 'server';
    }
    return 'generic';
  };

  return (
    <div className="min-h-screen">
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl">
            <ErrorState
              type={getErrorType(error)}
              message={error || "We couldn't load your dashboard. Please try again."}
              onRetry={onRetry}
              retryText="Reload Dashboard"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
